/* eslint-disable no-unused-vars */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listEvalRuns, getEvalRun } from "../src/services/evaluationsApi.js";
import { Card, CardContent, CardHeader, CardTitle } from "../src/components/ui/card.jsx";
import { Input } from "../src/components/ui/input.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../src/components/ui/tabs.jsx";
import { formatDateTimeInTz } from "../src/utils/index.js";
import { RefreshCw, Info, ListFilter, X } from "lucide-react";
import * as XLSX from 'xlsx';
import { getEvaluatorDescription, getEvaluatorCategory } from "../src/constants/evaluatorDescriptions.js";
import { useSearchParams } from "react-router-dom";

// Stable interval that doesn't recreate on every render
function useInterval(callback, delayMs) {
  const savedCallback = useRef(callback);
  useEffect(() => { savedCallback.current = callback; }, [callback]);
  useEffect(() => {
    if (delayMs == null) return;
    const id = setInterval(() => savedCallback.current && savedCallback.current(), delayMs);
    return () => clearInterval(id);
  }, [delayMs]);
}

// Custom evaluators with predefined configurations for known domain-specific evaluators
const predefinedCustomConfigs = [
  {
    key: 'numerical_correctness',
    label: 'Numerical Correctness',
    exactAgg: 'numerical_correctness.score',
    rowKeys: ['outputs.numerical_correctness.score', 'outputs.numeric_correctness.score', 'numerical_correctness.score', 'numeric_correctness.score'],
    aggKeys: ['numerical_correctness.score', 'numeric_correctness.score'],
    max: 5,
    pass: (v) => v >= 4, // Must score well
    yMin: 1,
    yMax: 5,
    transform: (v) => v <= 1 ? 1 + 4 * v : v, // Convert 0-1 scale to 1-5 if needed
  },
  {
    key: 'citation_accuracy',
    label: 'Citation Accuracy',
    exactAgg: 'citation_accuracy.score',
    rowKeys: ['outputs.citation_accuracy.score', 'citation_accuracy.score'],
    aggKeys: ['citation_accuracy.score'],
    max: 5,
    pass: (v) => v >= 4,
    yMin: 1,
    yMax: 5,
    transform: (v) => v <= 1 ? 1 + 4 * v : v, // Convert 0-1 scale to 1-5 if needed
  },
  {
    key: 'regulatory_compliance',
    label: 'Regulatory Compliance',
    exactAgg: 'regulatory_compliance.score',
    rowKeys: ['outputs.regulatory_compliance.score', 'regulatory_compliance.score'],
    aggKeys: ['regulatory_compliance.score'],
    max: 5,
    pass: (v) => v >= 4,
    yMin: 1,
    yMax: 5,
    transform: (v) => v <= 1 ? 1 + 4 * v : v, // Convert 0-1 scale to 1-5 if needed
  },
  {
    key: 'technical_terminology',
    label: 'Technical Terminology',
    exactAgg: 'technical_terminology.score',
    rowKeys: ['outputs.technical_terminology.score', 'technical_terminology.score'],
    aggKeys: ['technical_terminology.score'],
    max: 5,
    pass: (v) => v >= 4,
    yMin: 1,
    yMax: 5,
    transform: (v) => v <= 1 ? 1 + 4 * v : v, // Convert 0-1 scale to 1-5 if needed
  },
  {
    key: 'content_filtering',
    label: 'Content Filtering',
    exactAgg: 'content_filtering.score',
    rowKeys: ['outputs.content_filtering.score', 'content_filtering.score'],
    aggKeys: ['content_filtering.score'],
    max: 5,
    pass: (v) => v >= 4, // Must be safe
    yMin: 1,
    yMax: 5,
    transform: (v) => v <= 1 ? 1 + 4 * v : v, // Convert 0-1 scale to 1-5 if needed
  },
];

export default function EvaluationResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [runs, setRuns] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [trendData, setTrendData] = useState({});
  const [trendLoading, setTrendLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('metrics'); // 'metrics' | 'options'
  const [qualityFilter, setQualityFilter] = useState('assisted'); // 'assisted' | 'nlp' | 'custom'
  const [customConfigs, setCustomConfigs] = useState(predefinedCustomConfigs);

  // Add filter state variables similar to TestCases and EvaluationRuns
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [environmentFilter, setEnvironmentFilter] = useState(searchParams.get("environment") || "all");
  const [ragFilter, setRagFilter] = useState(searchParams.get("rag") || "all");
  const [evaluatorFilter, setEvaluatorFilter] = useState(searchParams.get("evaluator") || "all");
  const [timeRangeFilter, setTimeRangeFilter] = useState(searchParams.get("timeRange") || "all");
  const [showFilters, setShowFilters] = useState(false);

  // Collection filtering state
  const [collectionFilter, setCollectionFilter] = useState(searchParams.get("collection") || "all");
  const [remoteCollections, setRemoteCollections] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [orgIdForCollections, setOrgIdForCollections] = useState("");
  // Removed runCollectionMatches - now using stored collection_id in run options

  // Keep a ref to avoid useCallback/runaway dependency loops
  const runsRef = useRef(runs);
  useEffect(() => { runsRef.current = runs; }, [runs]);
  const lastTrendBuildAtRef = useRef(0);

  // Sync filters to URL query params
  useEffect(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (environmentFilter !== "all") params.environment = environmentFilter;
    if (ragFilter !== "all") params.rag = ragFilter;
    if (evaluatorFilter !== "all") params.evaluator = evaluatorFilter;
    if (timeRangeFilter !== "all") params.timeRange = timeRangeFilter;
    if (collectionFilter !== "all") params.collection = collectionFilter;
    setSearchParams(params);
  }, [statusFilter, environmentFilter, ragFilter, evaluatorFilter, timeRangeFilter, collectionFilter, setSearchParams]);

  // Helper function to fetch full run data (including metadata) for a run
  const fetchFullRunData = async (runId) => {
    try {
      const fullData = await getEvalRun(runId);
      return fullData;
    } catch (e) {
      // Full data not available, that's okay
    }
    return null;
  };

  const loadRuns = useCallback(async (forceRefresh = false) => {
    try {
      setError("");
      const list = await listEvalRuns(50);
      
      // Only enrich runs if we don't have them already or if force refresh is requested
      if (forceRefresh || runsRef.current.length === 0) {
        // Enrich runs with metadata and add row count as test count
        const enrichedList = await Promise.all(
          list.map(async (run) => {
            const needsOptions = !run.options;
            
            if (needsOptions) {
              const fullData = await fetchFullRunData(run.id);
              if (fullData) {
                const enriched = { ...run };
                if (fullData.options) {
                  enriched.options = fullData.options;
                }
                // Add test count from actual results rows
                const rowCount = fullData.results?.rows?.length || fullData.rows?.length || 0;
                if (rowCount > 0) {
                  enriched.testCount = rowCount;
                }
                return enriched;
              }
            }
            return run;
          })
        );
        // Avoid unnecessary state churn if unchanged by shallow compare on id/status/updated_at
        const prev = runsRef.current || [];
        const same = prev.length === enrichedList.length && prev.every((p, i) => p.id === enrichedList[i].id && p.status === enrichedList[i].status && (p.updated_at || '') === (enrichedList[i].updated_at || ''));
        if (!same) setRuns(enrichedList);
        if (!selectedId && enrichedList.length) setSelectedId(enrichedList[0].id);
      } else {
        // Just update the status and basic info without re-enriching
        const updatedList = list.map(newRun => {
          const existingRun = runsRef.current.find(r => r.id === newRun.id);
          if (existingRun) {
            // Preserve enriched data, only update status and basic fields
            return {
              ...existingRun,
              status: newRun.status,
              created_at: newRun.created_at,
              updated_at: newRun.updated_at,
              // Keep existing options, testCount, etc.
            };
          }
          return newRun;
        });
        // Avoid set if nothing changed
        const prev = runsRef.current || [];
        const same = prev.length === updatedList.length && prev.every((p, i) => p.id === updatedList[i].id && p.status === updatedList[i].status && (p.updated_at || '') === (updatedList[i].updated_at || ''));
        if (!same) setRuns(updatedList);
      }
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [selectedId]);

  const inFlightControllerRef = useRef(null);
  const loadSelected = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      if (inFlightControllerRef.current) {
        try { inFlightControllerRef.current.abort(); } catch (e) { /* swallow */ }
      }
      const controller = new AbortController();
      inFlightControllerRef.current = controller;
      const data = await getEvalRun(id, { signal: controller.signal });
      setSelected(data);
      // Immediately sync the left runs list with the latest selected details
      setRuns((prev) => {
        const idx = prev.findIndex((r) => r.id === id);
        if (idx === -1) return prev;
        const current = prev[idx];
        const updated = { ...current };
        let changed = false;
        if (current.status !== data.status) { updated.status = data.status; changed = true; }
        if ((current.updated_at || '') !== (data.updated_at || '')) { updated.updated_at = data.updated_at; changed = true; }
        const rowCount = data?.results?.rows?.length || data?.rows?.length || 0;
        if (rowCount && current.testCount !== rowCount) { updated.testCount = rowCount; changed = true; }
        if (!current.options && data.options) { updated.options = data.options; changed = true; }
        if (!changed) return prev;
        const copy = prev.slice();
        copy[idx] = updated;
        return copy;
      });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  // Initial load once on mount; subsequent updates are driven by polling below
  // initial load on mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRuns(true); }, []);
  useEffect(() => { loadSelected(selectedId); }, [selectedId]);

  // Infer dominant org_id from test cases to fetch collections
  useEffect(() => {
    const getOrgIdFromTestCases = async () => {
      if (orgIdForCollections) return; // Already have it
      
      try {
        // Load some test cases to get org_id
        const { listEvaluations } = await import("../src/services/evaluationsApi.js");
        const testCases = await listEvaluations({ limit: 50 });
        
        // Count org_ids to find the dominant one
        const counts = {};
        for (const tc of testCases) {
          const oid = tc?.config_data?.org_id;
          if (oid) counts[oid] = (counts[oid] || 0) + 1;
        }
        
        const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
        if (dominant && dominant !== orgIdForCollections) {
          setOrgIdForCollections(dominant);
        }
      } catch (e) {
        console.error("Failed to get org_id from test cases:", e);
      }
    };
    
    getOrgIdFromTestCases();
  }, [orgIdForCollections]);

  // Fetch remote collections when org_id is available
  useEffect(() => {
    const fetchRemoteCollections = async (orgId) => {
      try {
        setRemoteLoading(true);
        console.log('🔄 Loading collections for org_id:', orgId);
        
        const qs = new URLSearchParams({ scope: 'org', org_id: orgId });
        const res = await fetch(`/api/collections?${qs.toString()}`);
        if (!res.ok) throw new Error(`List failed: ${res.status} ${res.statusText}`);
        
        const json = await res.json();
        const items = Array.isArray(json?.items) ? json.items : [];
        
        console.log('✅ Collections response:', { 
          count: items.length, 
          first: items[0],
          selectionBased: items.filter(c => Array.isArray(c.criteria?.ids)).length,
          response: json
        });
        
        setRemoteCollections(items);
      } catch (e) {
        console.error('❌ Failed to load shared collections:', e);
        setRemoteCollections([]);
      } finally {
        setRemoteLoading(false);
      }
    };
    
    if (orgIdForCollections) {
      console.log('🚀 Starting collection fetch for org_id:', orgIdForCollections);
      fetchRemoteCollections(orgIdForCollections);
    } else {
      console.log('⚠️  No org_id available for collections');
    }
  }, [orgIdForCollections]);

  // Removed complex collection matching logic - now using stored collection_id

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('exportDropdown');
      const button = event.target.closest('button');
      if (dropdown && !dropdown.contains(event.target) && !button?.onclick) {
        dropdown.classList.add('hidden');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Separate polling for running evaluations - only update the specific running evaluation
  // Adaptive polling for the selected run: only while active; otherwise disabled
  const selectedIsActive = Boolean(selectedId && (selected?.status === 'queued' || selected?.status === 'started' || selected?.status === 'running'));
  const selectedPollDelay = selectedIsActive ? 15000 : null; // 15s when active, off when idle
  useInterval(() => { loadSelected(selectedId); }, selectedPollDelay);

  // Separate, less frequent polling for the runs list (only when needed)
  // Adaptive polling for runs list: only while any run is active; otherwise disabled
  const anyActiveNow = (runsRef.current || []).some(run => run.status === 'queued' || run.status === 'started' || run.status === 'running') || selectedIsActive;
  const listPollDelay = anyActiveNow ? 120000 : null; // 2 min when active, off when idle
  useInterval(() => { loadRuns(); }, listPollDelay);

  const filteredRuns = useMemo(() => {
    return runs
      .filter(r => !String(r.id).startsWith('meta_'))
      .filter(r => {
        // Search filter
        if (search && !String(r.id).includes(search) && 
            !(r.options?.notes || r.options?.title || r.title || r.notes || '').toLowerCase().includes(search.toLowerCase())) {
          return false;
        }
        
        // Status filter
        if (statusFilter !== "all" && r.status !== statusFilter) {
          return false;
        }
        
        // Environment filter
        if (environmentFilter !== "all") {
          const environment = r.options?.environment || r.environment;
          if (environment !== environmentFilter) {
            return false;
          }
        }
        
        // RAG filter
        if (ragFilter !== "all") {
          const useRag = r.options?.use_rag ?? r.use_rag;
          if (ragFilter === "enabled" && useRag !== true) return false;
          if (ragFilter === "disabled" && useRag !== false) return false;
        }
        
        // Evaluator filter (check if run includes specific evaluator)
        if (evaluatorFilter !== "all") {
          const evaluators = r.options?.evaluators || r.evaluators || [];
          if (!Array.isArray(evaluators) || !evaluators.includes(evaluatorFilter)) {
            return false;
          }
        }
        
        // Time range filter
        if (timeRangeFilter !== "all") {
          const runDate = new Date(r.updated_at || r.created_at);
          const now = new Date();
          const diffHours = (now - runDate) / (1000 * 60 * 60);
          
          switch (timeRangeFilter) {
            case "1h":
              if (diffHours > 1) return false;
              break;
            case "6h":
              if (diffHours > 6) return false;
              break;
            case "24h":
              if (diffHours > 24) return false;
              break;
            case "7d":
              if (diffHours > 24 * 7) return false;
              break;
            case "30d":
              if (diffHours > 24 * 30) return false;
              break;
          }
        }
        
        // Collection filter - use stored collection_id from run options
        if (collectionFilter !== "all") {
          const runCollectionId = r.options?.collection_id;
          if (runCollectionId !== collectionFilter) {
            return false;
          }
        }
        
        return true;
      });
  }, [runs, search, statusFilter, environmentFilter, ragFilter, evaluatorFilter, timeRangeFilter, collectionFilter]);

  // Clear selected run when no runs match filters
  useEffect(() => {
    if (filteredRuns.length === 0 && selectedId) {
      setSelectedId("");
      setSelected(null);
    } else if (filteredRuns.length > 0 && selectedId && !filteredRuns.find(r => r.id === selectedId)) {
      // If current selection is not in filtered runs, select the first one
      setSelectedId(filteredRuns[0].id);
    }
  }, [filteredRuns, selectedId]);

  // Helper functions to get unique values for filters
  const getUniqueStatuses = () => {
    const statuses = new Set(runs.map(r => r.status).filter(Boolean));
    return Array.from(statuses).sort();
  };

  const getUniqueEnvironments = () => {
    const environments = new Set(runs.map(r => r.options?.environment || r.environment).filter(Boolean));
    return Array.from(environments).sort();
  };

  const getUniqueEvaluators = () => {
    const evaluators = new Set();
    runs.forEach(r => {
      const runEvaluators = r.options?.evaluators || r.evaluators || [];
      if (Array.isArray(runEvaluators)) {
        runEvaluators.forEach(evaluator => evaluators.add(evaluator));
      }
    });
    return Array.from(evaluators).sort();
  };

  const handleClearFilters = () => {
    setStatusFilter("all");
    setEnvironmentFilter("all");
    setRagFilter("all");
    setEvaluatorFilter("all");
    setTimeRangeFilter("all");
    setCollectionFilter("all");
    setSearch("");
  };

  const hasActiveFilters = statusFilter !== "all" || environmentFilter !== "all" || 
    ragFilter !== "all" || evaluatorFilter !== "all" || timeRangeFilter !== "all" || 
    collectionFilter !== "all" || search;

  // Generic helpers to find metric values
  const getValueByPath = (obj, path) => path.split('.').reduce((acc, part) => (acc && acc[part] != null ? acc[part] : undefined), obj);
  const isNumeric = (v) => typeof v === 'number' && !Number.isNaN(v);
  const isPassRateKey = (key) => /pass|rate|count/i.test(key);

  // Helpers to read metric values from row objects
  const getRowMetric = (row, cfg) => {
    const keys = cfg.rowKeys || cfg.valueKeys || [];
    for (const k of keys) {
      let v = row[k] != null ? row[k] : getValueByPath(row, k);
      // Coerce booleans to numeric 0/1 for metrics like correctness
      if (typeof v === 'boolean') v = v ? 1 : 0;
      if (isNumeric(v)) return Number(v);
    }
    // Deep scan row for nested numeric fields that match the metric name and are not pass/rate/count
    const scan = (obj, pathPrefix = '') => {
      for (const [k, v] of Object.entries(obj || {})) {
        const path = pathPrefix ? `${pathPrefix}.${k}` : k;
        if (v && typeof v === 'object') {
          const inner = scan(v, path);
          if (isNumeric(inner)) return inner;
        } else if (isNumeric(v)) {
          const lower = path.toLowerCase();
          if (lower.includes(cfg.key) && !lower.startsWith('inputs') && !isPassRateKey(lower)) {
            return Number(v);
          }
        }
      }
      return undefined;
    };
    return scan(row);
  };

  // Define assisted and NLP metric configs
  const assistedConfigs = [
    {
      key: 'similarity',
      label: 'Similarity',
      exactAgg: 'similarity.similarity',
      rowKeys: ['outputs.similarity.similarity', 'outputs.similarity.score', 'similarity.similarity', 'similarity.score'],
      aggKeys: ['similarity.similarity', 'similarity.gpt_similarity'],
      max: 5,
      pass: (v) => v >= 4,
      yMin: 1,
      yMax: 5,
      scaleToFive: true,
    },
    {
      key: 'groundedness',
      label: 'Groundedness',
      exactAgg: 'groundedness.groundedness',
      rowKeys: ['outputs.groundedness.groundedness', 'outputs.groundedness.score', 'groundedness.groundedness', 'groundedness.score'],
      aggKeys: ['groundedness.groundedness', 'groundedness.gpt_groundedness'],
      max: 5,
      pass: (v) => v >= 4,
      yMin: 1,
      yMax: 5,
    },
    {
      key: 'relevance',
      label: 'Relevance',
      exactAgg: 'relevance.relevance',
      rowKeys: ['outputs.relevance.relevance', 'outputs.relevance.score', 'relevance.relevance', 'relevance.score'],
      aggKeys: ['relevance.relevance', 'relevance.gpt_relevance'],
      max: 5,
      pass: (v) => v >= 4,
      yMin: 1,
      yMax: 5,
    },
    {
      key: 'coherence',
      label: 'Coherence',
      exactAgg: 'coherence.coherence',
      rowKeys: ['outputs.coherence.coherence', 'outputs.coherence.score', 'coherence.coherence', 'coherence.score'],
      aggKeys: ['coherence.coherence', 'coherence.gpt_coherence'],
      max: 5,
      pass: (v) => v >= 4,
      yMin: 1,
      yMax: 5,
    },
    {
      key: 'fluency',
      label: 'Fluency',
      exactAgg: 'fluency.fluency',
      rowKeys: ['outputs.fluency.fluency', 'outputs.fluency.score', 'fluency.fluency', 'fluency.score'],
      aggKeys: ['fluency.fluency', 'fluency.gpt_fluency'],
      max: 5,
      pass: (v) => v >= 4,
      yMin: 1,
      yMax: 5,
    },
    {
      key: 'retrieval_quality',
      label: 'Retrieval Quality',
      exactAgg: 'retrieval.quality',
      rowKeys: [
        'outputs.retrieval.retrieval', 'outputs.retrieval.quality', 'outputs.retrieval.score', 'outputs.retrieval_quality.score', 'outputs.retrieval_quality.quality',
        'retrieval.retrieval', 'retrieval.quality', 'retrieval.score', 'retrieval_quality.score', 'retrieval_quality.quality',
        'outputs.retrieval', 'retrieval'
      ],
      aggKeys: ['retrieval.retrieval', 'retrieval.quality', 'retrieval.retrieval_quality', 'retrieval_quality.score', 'retrieval.score'],
      max: 5,
      pass: (v) => v >= 4,
      yMin: 1,
      yMax: 5,
    },
    {
      key: 'qa',
      label: 'Question Answering',
      exactAgg: 'qa.score',
      rowKeys: ['outputs.qa.score', 'outputs.question_answering.score', 'qa.score', 'question_answering.score'],
      aggKeys: ['qa.score', 'question_answering.score'],
      max: 5,
      pass: (v) => v >= 4,
      yMin: 1,
      yMax: 5,
    },
  ];
  const nlpConfigs = [
    {
      key: 'rouge_precision',
      label: 'ROUGE precision',
      exactAgg: 'rouge.rouge_precision',
      rowKeys: ['outputs.rouge.rouge_precision', 'rouge.rouge_precision', 'outputs.rouge.precision', 'rouge.precision'],
      aggKeys: ['rouge.rouge_precision', 'rouge.precision'],
      max: 1,
      pass: (v) => v >= 0.2,
      yMin: 0,
      yMax: 1,
    },
    {
      key: 'rouge_recall',
      label: 'ROUGE recall',
      exactAgg: 'rouge.rouge_recall',
      rowKeys: ['outputs.rouge.rouge_recall', 'rouge.rouge_recall', 'outputs.rouge.recall', 'rouge.recall'],
      aggKeys: ['rouge.rouge_recall', 'rouge.recall'],
      max: 1,
      pass: (v) => v >= 0.2,
      yMin: 0,
      yMax: 1,
    },
    {
      key: 'rouge_f1_score',
      label: 'ROUGE F1 score',
      exactAgg: 'rouge.rouge_f1_score',
      rowKeys: ['outputs.rouge.rouge_f1_score', 'rouge.rouge_f1_score', 'outputs.rouge.f1', 'rouge.f1'],
      aggKeys: ['rouge.rouge_f1_score', 'rouge.f1', 'rouge.f1_score'],
      max: 1,
      pass: (v) => v >= 0.2,
      yMin: 0,
      yMax: 1,
    },
    {
      key: 'rouge',
      label: 'ROUGE-1',
      exactAgg: 'rouge.rouge_1',
      rowKeys: ['outputs.rouge.rouge_1', 'outputs.rouge.rouge1', 'rouge.rouge_1', 'rouge.rouge1', 'outputs.rouge.score', 'rouge.score'],
      aggKeys: ['rouge.rouge_1', 'rouge.rouge1', 'rouge.score'],
      max: 1,
      pass: (v) => v >= 0.2,
      yMin: 0,
      yMax: 1,
    },
    {
      key: 'f1_score',
      label: 'F1 Score',
      exactAgg: 'f1.score',
      rowKeys: ['outputs.f1.score', 'outputs.f1_score.score', 'f1.score', 'f1_score.score'],
      aggKeys: ['f1.score', 'f1_score.score'],
      max: 1,
      pass: (v) => v >= 0.5,
      yMin: 0,
      yMax: 1,
    },
    {
      key: 'bleu',
      label: 'BLEU Score',
      exactAgg: 'bleu.score',
      rowKeys: ['outputs.bleu.score', 'outputs.bleu.bleu', 'bleu.score', 'bleu.bleu'],
      aggKeys: ['bleu.score', 'bleu.bleu'],
      max: 1,
      pass: (v) => v >= 0.3,
      yMin: 0,
      yMax: 1,
    },
  ];
  const allMetricConfigs = [...assistedConfigs, ...nlpConfigs, ...predefinedCustomConfigs];

  // Utilities for custom evaluator discovery
  const titleize = (name) => String(name || '')
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // Canonicalize evaluator names to avoid duplicates like numeric_* vs numerical_*
  const canonicalizeEvaluatorName = (rawName) => {
    const n = String(rawName || '').toLowerCase().trim().replace(/[\s\-.]+/g, '_');
    // Handle numerical correctness variants (including llm suffixes)
    if (n.match(/^(numeric|numerical)_?correctness(_llm)?$/)) {
      return 'numerical_correctness';
    }
    // Handle retrieval variants  
    if (n === 'retrieval' || n === 'retrieval_quality') {
      return 'retrieval_quality';
    }
    return n;
  };

  const labelForEvaluatorName = (canonicalName) => {
    if (canonicalName === 'numerical_correctness') return 'Numerical Correctness';
    return titleize(canonicalName);
  };

  const builtinEvaluatorNames = new Set([
    'similarity', 'groundedness', 'relevance', 'coherence', 'fluency',
    'retrieval_quality', 'retrieval', 'qa', 'question_answering',
    'rouge', 'rouge_1', 'rouge_precision', 'rouge_recall', 'rouge_f1_score',
    'f1_score', 'bleu',
    // Custom evaluators that should be treated as predefined rather than dynamically detected
    'numerical_correctness', 'citation_accuracy', 'regulatory_compliance', 
    'technical_terminology', 'content_filtering'
  ]);

  const detectCustomConfigsFromRows = (rows = []) => {
    const found = new Map(); // canonicalName -> { values: [], metricKeys: Set, variants: Set }
    const visit = (obj, prefix = '') => {
      if (!obj || typeof obj !== 'object') return;
      for (const [k, v] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${k}` : k;
        if (v && typeof v === 'object') {
          visit(v, path);
          continue;
        }
        // collect numeric and boolean leaf values under outputs.*
        if (!path.startsWith('outputs.')) continue;
        const parts = path.split('.');
        if (parts.length < 3) continue; // outputs.<name>.<metric>
        const rawName = parts[1];
        if (!rawName) continue;
        const canonical = canonicalizeEvaluatorName(rawName);
        if (!canonical || builtinEvaluatorNames.has(canonical)) continue;
        if (!found.has(canonical)) found.set(canonical, { values: [], metricKeys: new Set(), variants: new Set() });
        const entry = found.get(canonical);
        const num = typeof v === 'boolean' ? (v ? 1 : 0) : v;
        if (typeof num === 'number' && !Number.isNaN(num)) {
          entry.values.push(num);
          entry.metricKeys.add(parts.slice(2).join('.'));
          entry.variants.add(rawName);
        }
      }
    };
    for (const row of rows) {
      visit(row);
      // Also cover flattened keys (if any)
      for (const [k, v] of Object.entries(row || {})) {
        if (!k || typeof k !== 'string' || !k.startsWith('outputs.')) continue;
        const parts = k.split('.');
        if (parts.length < 3) continue;
        const rawName = parts[1];
        const canonical = canonicalizeEvaluatorName(rawName);
        if (!canonical || builtinEvaluatorNames.has(canonical)) continue;
        if (!found.has(canonical)) found.set(canonical, { values: [], metricKeys: new Set(), variants: new Set() });
        const entry = found.get(canonical);
        const num = typeof v === 'boolean' ? (v ? 1 : 0) : v;
        if (typeof num === 'number' && !Number.isNaN(num)) {
          entry.values.push(num);
          entry.metricKeys.add(parts.slice(2).join('.'));
          entry.variants.add(rawName);
        }
      }
    }
    const configs = [];
    for (const [canonical, info] of found.entries()) {
      if (!info.values.length) continue;
      const maxObs = Math.max(...info.values);
      const preferredMetric = info.metricKeys.has('score') ? 'score' : [...info.metricKeys][0];
      const label = labelForEvaluatorName(canonical);
      // Build row/agg keys to cover all seen variants plus the canonical form
      const variantList = Array.from(new Set([canonical, ...info.variants].map((n) => canonicalizeEvaluatorName(n))));
      const rowKeys = [];
      const aggKeys = [];
      for (const variant of variantList) {
        rowKeys.push(`outputs.${variant}.${preferredMetric}`, `${variant}.${preferredMetric}`);
        aggKeys.push(`${variant}.${preferredMetric}`, `metrics.${variant}.${preferredMetric}`);
      }
      const baseCfg = {
        key: `custom_${canonical}`,
        label,
        exactAgg: `${canonical}.${preferredMetric}`,
        rowKeys,
        aggKeys,
      };
      // Enforce 1..5 scale for all custom evaluators. If incoming looks 0..1, apply transform.
      const needsTransform = maxObs <= 1.01;
      const cfg = {
        ...baseCfg,
        ...(needsTransform ? { transform: (v) => 1 + 4 * v } : {}),
        max: 5,
        pass: (v) => v >= 4,
        yMin: 1,
        yMax: 5,
      };
      configs.push(cfg);
    }
    return configs;
  };

  // Keep customConfigs in sync with the selected run's rows
  useEffect(() => {
    const rows = selected?.results?.rows || selected?.rows || [];
    const detected = detectCustomConfigsFromRows(rows);
    // Merge predefined configs with detected ones, avoiding duplicates
    const byKey = new Map();
    [...predefinedCustomConfigs, ...detected].forEach((c) => { byKey.set(c.key, c); });
    const merged = [...byKey.values()];
    // Only update if changed by shallow key compare
    const same = merged.length === customConfigs.length && merged.every((d, i) => {
      const existing = customConfigs.find(e => e.key === d.key);
      return existing && existing.key === d.key;
    });
    if (!same) setCustomConfigs(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const summarizeCategory = (rows, configs) => {
    const total = rows?.length || 0;
    return configs.map((cfg) => {
      const rawValues = (rows || [])
        .map((r) => getRowMetric(r, cfg))
        .filter((v) => typeof v === 'number' && !Number.isNaN(v));
      const values = rawValues.map((v) => (typeof cfg.transform === 'function' ? cfg.transform(v) : v));
      const normalized = values.map((v) => Math.max(0, Math.min(1, v / cfg.max)));
      const avg = values.length ? normalized.reduce((a, b) => a + b, 0) / normalized.length : 0;
      const passed = values.filter((v) => cfg.pass(v)).length;
      return { ...cfg, avg, count: values.length, passed, total };
    });
  };

  const assistedSummary = summarizeCategory(selected?.results?.rows || selected?.rows, assistedConfigs);
  const nlpSummary = summarizeCategory(selected?.results?.rows || selected?.rows, nlpConfigs);
  const customSummary = summarizeCategory(selected?.results?.rows || selected?.rows, customConfigs);

  const metricBar = (value01) => (
    <div className="h-6 w-full rounded bg-slate-200 dark:bg-slate-700 overflow-hidden">
      <div className={`h-full ${value01 >= 0.5 ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ width: `${Math.round(value01 * 100)}%` }} />
    </div>
  );

  const getMetricFromAggregate = (metricsObj, cfg) => {
    if (!metricsObj) return undefined;
    if (cfg.exactAgg && metricsObj[cfg.exactAgg] != null && isNumeric(metricsObj[cfg.exactAgg])) {
      return Number(metricsObj[cfg.exactAgg]);
    }
    const aggKeys = cfg.aggKeys || [];
    for (const key of aggKeys) {
      if (metricsObj[key] != null && isNumeric(metricsObj[key])) return Number(metricsObj[key]);
      const nested = getValueByPath(metricsObj, key);
      if (isNumeric(nested)) return Number(nested);
    }
    return undefined;
  };

  const computeRunMetricValue01 = (runResource, cfg) => {
    const res = computeRunMetricRaw(runResource, cfg);
    if (!res || typeof res.raw !== 'number') return undefined;
    const normalized = Math.max(0, Math.min(1, res.raw / cfg.max));
    return normalized;
  };

  const computeRunMetricRaw = (runResource, cfg) => {
    const metricsObj = runResource?.results?.metrics || runResource?.metrics;
    // Prefer exact aggregate key if present
    if (metricsObj && cfg.exactAgg && isNumeric(metricsObj[cfg.exactAgg])) {
      const base = Number(metricsObj[cfg.exactAgg]);
      const transformed = typeof cfg.transform === 'function' ? cfg.transform(base) : base;
      return { raw: transformed, source: `results.metrics[${cfg.exactAgg}]` };
    }
    if (metricsObj && Array.isArray(cfg.aggKeys)) {
      for (const key of cfg.aggKeys) {
        const direct = metricsObj[key];
        if (isNumeric(direct)) {
          const base = Number(direct);
          const transformed = typeof cfg.transform === 'function' ? cfg.transform(base) : base;
          return { raw: transformed, source: `results.metrics[${key}]` };
        }
        const nested = getValueByPath(metricsObj, key);
        if (isNumeric(nested)) {
          const base = Number(nested);
          const transformed = typeof cfg.transform === 'function' ? cfg.transform(base) : base;
          return { raw: transformed, source: `results.metrics.${key}` };
        }
      }
    }
    // Fallback to averaging row-level values
    const rows = runResource?.results?.rows || runResource?.rows || [];
    const values = rows
      .map((r) => getRowMetric(r, cfg))
      .filter((v) => typeof v === 'number' && !Number.isNaN(v))
      .map((v) => (typeof cfg.transform === 'function' ? cfg.transform(v) : v));
    if (!values.length) return undefined;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    return { raw: avg, source: 'rows.average' };
  };

  const buildTrendData = async (runsList) => {
    setTrendLoading(true);
    console.log('📊 Building trend data for', runsList.length, 'runs', {
      totalRuns: runs.length,
      filteredRuns: runsList.length,
      collectionFilter,
      hasCollectionFilter: collectionFilter !== "all"
    });
    try {
      const recent = (runsList || []).slice(0, 20);
      const details = await Promise.all(recent.map((r) => getEvalRun(r.id).catch(() => null)));
      // Base configs + dynamic custom configs discovered across all recent runs
      let dynamicCustom = [];
      try {
        const allRows = details.flatMap((d) => (d?.results?.rows || d?.rows || []));
        dynamicCustom = detectCustomConfigsFromRows(allRows);
  } catch (e) { /* ignore custom detection errors */ }
      // update customConfigs state if it changed (union with current for stability)
      if (dynamicCustom.length) {
        const byKey = new Map();
        [...customConfigs, ...dynamicCustom].forEach((c) => { byKey.set(c.key, c); });
        const merged = [...byKey.values()];
        const same = merged.length === customConfigs.length && merged.every((d, i) => d.key === customConfigs[i].key);
        if (!same) setCustomConfigs(merged);
      }

      const combinedConfigs = [...assistedConfigs, ...nlpConfigs, ...customConfigs, ...dynamicCustom]
        .filter((v, i, arr) => arr.findIndex((x) => x.key === v.key) === i);

      const byMetric = {};
      for (const cfg of combinedConfigs) byMetric[cfg.key] = [];
      details.forEach((res, idx) => {
        if (!res) return;
        const runMeta = recent[idx];
        for (const cfg of combinedConfigs) {
          const result = computeRunMetricRaw(res, cfg);
          if (result && typeof result.raw === 'number') {
            const when = new Date(res.updated_at || res.created_at || runMeta.updated_at || runMeta.created_at || Date.now()).getTime();
            byMetric[cfg.key].push({ id: runMeta.id, t: when, v01: result.raw, raw: result.raw, source: result.source });
          }
        }
      });
      setTrendData(byMetric);
    } finally {
      setTrendLoading(false);
    }
  };

  const lastTrendSignatureRef = useRef('');
  const lastCollectionFilterRef = useRef('');
  useEffect(() => {
    if (!filteredRuns.length) {
      // Clear trend data when no filtered runs
      setTrendData({});
      return;
    }
    // Build a compact signature of ids + updated_at to detect real changes
    const sig = filteredRuns.map(r => `${r.id}:${r.updated_at || ''}:${r.status}`).join('|');
    // Force rebuild if collection filter changed
    const collectionChanged = collectionFilter !== lastCollectionFilterRef.current;
    if (!collectionChanged && sig === lastTrendSignatureRef.current) return;
    
    const now = Date.now();
    // Reduce throttle to 5s and bypass throttle if collection filter changed
    if (!collectionChanged && (now - (lastTrendBuildAtRef.current || 0) < 5000)) return;
    
    lastTrendSignatureRef.current = sig;
    lastCollectionFilterRef.current = collectionFilter;
    lastTrendBuildAtRef.current = now;
    
    console.log('🔄 Rebuilding trends due to:', { 
      collectionChanged, 
      filteredRunsCount: filteredRuns.length,
      collectionFilter,
      runIds: filteredRuns.map(r => r.id).slice(0, 3)
    });
    
    buildTrendData(filteredRuns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRuns, collectionFilter]);

  const Sparkline = ({ series, stroke = '#10b981' }) => {
    const width = 180; const height = 40; const padding = 4;
    const points = (series || []).sort((a, b) => a.t - b.t);
    if (!points.length) return <div className="h-10" />;
    const xs = points.map((_, i) => padding + (i * (width - 2 * padding)) / Math.max(1, points.length - 1));
    const ys = points.map((p) => height - padding - p.v01 * (height - 2 * padding));
    const d = xs.map((x, i) => `${i ? 'L' : 'M'}${x},${ys[i]}`).join(' ');
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <path d={d} fill="none" stroke={stroke} strokeWidth="2" />
      </svg>
    );
  };

  const SparklineWithDots = ({
    series,
    stroke = '#10b981',
    dot = '#10b981',
    onPointClick,
    valueFormatter,
    yMin: yMinProp,
    yMax: yMaxProp,
    showYAxis = true,
    tickCount = 3,
    invertYAxis = false,
  }) => {
    const [hoverIdx, setHoverIdx] = React.useState(null);

    // Canvas and layout
    const width = 240;
    const height = 72;
    const padding = 8;
    const axisPadding = showYAxis ? 28 : 8; // a bit more room so tick labels aren't clipped

    const points = (series || []).slice().sort((a, b) => a.t - b.t);
    if (!points.length) return <div className="h-20" />;

    // X positions are evenly spaced across available width
    const xs = points.map((_, i) =>
      axisPadding + (i * (width - axisPadding - padding)) / Math.max(1, points.length - 1)
    );

    // Y scaling based on provided min/max, or observed range
    const raws = points.map((p) => (typeof p.raw === 'number' ? p.raw : 0));
    const minRawObserved = Math.min(...raws);
    const maxRawObserved = Math.max(...raws);
    const yMin = typeof yMinProp === 'number' ? yMinProp : Math.min(0, minRawObserved);
    const yMax = typeof yMaxProp === 'number' ? yMaxProp : maxRawObserved;
    const range = yMax - yMin || 1;

    const ys = points.map((p) => {
      const raw = typeof p.raw === 'number' ? p.raw : 0;
      const ratio = (raw - yMin) / range;
      return invertYAxis
        ? padding + ratio * (height - 2 * padding)
        : height - padding - ratio * (height - 2 * padding);
    });

    const d = xs.map((x, i) => `${i ? 'L' : 'M'}${x},${ys[i]}`).join(' ');

    const tooltip = (i) =>
      valueFormatter ? valueFormatter(points[i]) : `${String(points[i].raw ?? '')} (${points[i].source || 'unknown'})`;

    // Build y-axis ticks
    const ticks = [];
    if (showYAxis && tickCount > 0) {
      for (let i = 0; i < tickCount; i += 1) {
        const t = i / (tickCount - 1);
        const value = yMin + t * (yMax - yMin);
        const y = invertYAxis
          ? padding + t * (height - 2 * padding)
          : height - padding - t * (height - 2 * padding);
        ticks.push({ y, value });
      }
    }

    // Helper to position the tooltip so it doesn't get clipped
    const renderTooltip = (i, x, y) => {
      const boxW = 140;
      const boxH = 18;
      const pad = 6;

      // Prefer to the right & above the point
      let tx = x + pad;
      let ty = y - boxH;

      // Flip horizontally if overflowing on the right
      if (tx + boxW > width) tx = x - pad - boxW;
      // Flip vertically if overflowing at the top
      if (ty < 0) ty = y + pad;

      return (
        <g style={{ pointerEvents: 'none' }}>
          <rect x={tx} y={ty} width={boxW} height={boxH} rx={3} ry={3} fill="#111827" opacity="0.9" />
          <text x={tx + 4} y={ty + 12} fontSize="10" fill="#e5e7eb">{tooltip(i)}</text>
        </g>
      );
    };

    return (
      // Wrapper allows the SVG contents (ticks + tooltip) to overflow the card cell when needed
      <div className="relative" style={{ overflow: 'visible' }}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          style={{ overflow: 'visible' }}
        >
          {showYAxis && (
            <g>
              <line
                x1={axisPadding}
                y1={padding - 2}
                x2={axisPadding}
                y2={height - padding + 2}
                stroke="#cbd5e1"
                strokeWidth="1"
              />
              {ticks.map((tk, idx) => (
                <g key={idx}>
                  <line x1={axisPadding - 4} y1={tk.y} x2={axisPadding} y2={tk.y} stroke="#cbd5e1" strokeWidth="1" />
                  <text x={axisPadding - 6} y={tk.y + 3} textAnchor="end" fontSize="10" fill="#64748b">
                    {yMax <= 1 ? tk.value.toFixed(2).replace(/^0\./, '.') : Math.round(tk.value)}
                  </text>
                </g>
              ))}
            </g>
          )}

          <path d={d} fill="none" stroke={stroke} strokeWidth="2" />

          {xs.map((x, i) => (
            <g key={i} onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}>
              <circle
                cx={x}
                cy={ys[i]}
                r="3.5"
                fill={dot}
                style={{ cursor: onPointClick ? 'pointer' : 'default' }}
                onClick={() => onPointClick && onPointClick(points[i])}
              />
              {hoverIdx === i && renderTooltip(i, x, ys[i])}
            </g>
          ))}
        </svg>
      </div>
    );
  };

  const statusBadge = (status) => {
    const base = "px-2 py-0.5 rounded text-xs";
    const map = {
      queued: "bg-amber-100 text-amber-800",
      started: "bg-blue-100 text-blue-800",
      running: "bg-blue-100 text-blue-800",
      success: "bg-emerald-100 text-emerald-800",
      failed: "bg-rose-100 text-rose-800",
    };
    return <span className={`${base} ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
  };

  // Helper function to show run troubleshooting info
  const getRunTroubleshootingInfo = (run) => {
    if (run.status === 'started' || run.status === 'running') {
      const now = new Date();
      const updatedAt = new Date(run.updated_at || run.created_at);
      const timeSinceUpdate = Math.floor((now - updatedAt) / 1000 / 60); // minutes
      
      if (timeSinceUpdate > 5) {
        return (
          <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-200">
            ⚠️ Run has been {run.status} for {timeSinceUpdate} minutes. This may indicate the evaluation runner is not responding.
            <br />
            <details className="mt-1">
              <summary className="cursor-pointer text-amber-700 dark:text-amber-300">Show run details</summary>
              <div className="mt-1 text-xs">
                <div>Created: {new Date(run.created_at).toLocaleString()}</div>
                <div>Updated: {new Date(run.updated_at || run.created_at).toLocaleString()}</div>
                <div>Environment: {run.options?.environment || run.environment || 'unknown'}</div>
                <div>Concurrency: {run.options?.concurrency || 'unknown'}</div>
                {run.dataset_summary && <div>Test Cases: {run.dataset_summary.size}</div>}
                {run.error && <div className="text-rose-600">Error: {run.error}</div>}
              </div>
            </details>
          </div>
        );
      }
    }
    return null;
  };

  const metricDashboard = () => (
    <Tabs value={qualityFilter} onValueChange={setQualityFilter}>
      <TabsList>
        <TabsTrigger value="assisted">AI quality (AI Assisted)</TabsTrigger>
        <TabsTrigger value="nlp">AI quality (NLP)</TabsTrigger>
  {customConfigs.length > 0 && (<TabsTrigger value="custom">Custom evaluators</TabsTrigger>)}
      </TabsList>
      <TabsContent value="assisted" className="mt-3">
        <div className="grid md:grid-cols-4 gap-3">
          {assistedSummary.map((s) => {
            const description = getEvaluatorDescription(s.key);
            const category = getEvaluatorCategory(s.key);
            
            return (
              <div key={s.key} className="relative group">
                <div className="p-3 rounded-lg border bg-white dark:bg-slate-900/60">
                  <div className="flex items-center gap-1 mb-2">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{s.label}</div>
                    {description && <Info className="w-3 h-3 text-slate-400" />}
                  </div>
                  {metricBar(s.avg)}
                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{Math.round(s.avg * 100)}% avg</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">{s.passed}/{s.total || 0} passed</div>
                </div>
                
                {/* Tooltip */}
                {description && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 max-w-xs">
                    <div className="font-medium text-emerald-300 mb-1">{category}</div>
                    <div>{description}</div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </TabsContent>
      <TabsContent value="nlp" className="mt-3">
        <div className="grid md:grid-cols-3 gap-3">
          {nlpSummary.map((s) => {
            const description = getEvaluatorDescription(s.key);
            const category = getEvaluatorCategory(s.key);
            
            return (
              <div key={s.key} className="relative group">
                <div className="p-3 rounded-lg border bg-white dark:bg-slate-900/60">
                  <div className="flex items-center gap-1 mb-2">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{s.label}</div>
                    {description && <Info className="w-3 h-3 text-slate-400" />}
                  </div>
                  {metricBar(s.avg)}
                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{Math.round(s.avg * 100)}% avg</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">{s.passed}/{s.total || 0} passed</div>
                </div>
                
                {/* Tooltip */}
                {description && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 max-w-xs">
                    <div className="font-medium text-emerald-300 mb-1">{category}</div>
                    <div>{description}</div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </TabsContent>
      {customConfigs.length > 0 && (
        <TabsContent value="custom" className="mt-3">
          <div className="grid md:grid-cols-3 gap-3">
            {customSummary.map((s) => {
              const description = getEvaluatorDescription(s.key);
              const category = getEvaluatorCategory(s.key);
              
              return (
                <div key={s.key} className="relative group">
                  <div className="p-3 rounded-lg border bg-white dark:bg-slate-900/60">
                    <div className="flex items-center gap-1 mb-2">
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{s.label}</div>
                      {description && <Info className="w-3 h-3 text-slate-400" />}
                    </div>
                    {metricBar(s.avg)}
                    <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{Math.round(s.avg * 100)}% avg</div>
                    <div className="text-xs text-slate-600 dark:text-slate-400">{s.passed}/{s.total || 0} passed</div>
                  </div>
                  
                  {/* Tooltip */}
                  {description && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 max-w-xs">
                      <div className="font-medium text-emerald-300 mb-1">{category}</div>
                      <div>{description}</div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>
      )}
    </Tabs>
  );

  const rowsTable = () => {
    const rows = selected?.results?.rows || selected?.rows || [];
    if (!rows?.length) return <div className="text-sm text-slate-600 dark:text-slate-300">No row-level results.</div>;
    
    // Helper function to get metric value for a specific test case
    const getTestCaseMetric = (row, metricKey) => {
      // Use the qualityFilter state to determine which configs to use
      let configs;
      if (qualityFilter === 'assisted') {
        configs = assistedConfigs;
      } else if (qualityFilter === 'nlp') {
        configs = nlpConfigs;
      } else if (qualityFilter === 'custom') {
        configs = customConfigs;
      } else {
        return null;
      }
      
      const config = configs.find(cfg => cfg.key === metricKey);
      if (!config) return null;
      
      // Try to get the metric value from the row
      let value = getRowMetric(row, config);
      if (typeof value === 'number' && !Number.isNaN(value)) {
        // Apply transform if present (e.g., map 0..1 to 1..5)
        if (typeof config.transform === 'function') {
          value = config.transform(value);
        }
        return {
          value: value,
          normalized: Math.max(0, Math.min(1, value / config.max)),
          passed: config.pass(value)
        };
      }
      return null;
    };

    // Helper function to render metric badge
    const renderMetricBadge = (metricKey, metricData) => {
      if (!metricData) return null;
      
      // Find the config from the appropriate configs based on the current filter
      const configs = qualityFilter === 'assisted' ? assistedConfigs : nlpConfigs;
      const config = configs.find(cfg => cfg.key === metricKey);
      if (!config) return null;
      
      const { value, normalized, passed } = metricData;
      const percentage = Math.round(normalized * 100);
      const description = getEvaluatorDescription(metricKey);
      const category = getEvaluatorCategory(metricKey);
      
      return (
        <div key={metricKey} className="relative group">
          <div className="flex items-center gap-2 p-2 rounded-lg border dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <div className="text-xs font-medium text-slate-700 dark:text-slate-200">{config.label}</div>
                {description && <Info className="w-3 h-3 text-slate-400" />}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">{value.toFixed(2)}</div>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-16 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div 
                  className={`h-full ${passed ? 'bg-emerald-500' : 'bg-rose-500'}`} 
                  style={{ width: `${percentage}%` }} 
                />
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                passed 
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200' 
                  : 'bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200'
              }`}>
                {percentage}%
              </span>
            </div>
          </div>
          
          {/* Tooltip */}
          {description && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 max-w-xs">
              <div className="font-medium text-emerald-300 mb-1">{category}</div>
              <div>{description}</div>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-3">
        {rows.map((r, idx) => {
          // Get metrics for this test case based on filter
          let metrics = {};
          if (qualityFilter === 'assisted') {
            // AI Assisted metrics - get all from assistedConfigs
            assistedConfigs.forEach((cfg) => {
              metrics[cfg.key] = getTestCaseMetric(r, cfg.key);
            });
          } else if (qualityFilter === 'nlp') {
            // NLP metrics - get all from nlpConfigs
            nlpConfigs.forEach((cfg) => {
              metrics[cfg.key] = getTestCaseMetric(r, cfg.key);
            });
          } else if (qualityFilter === 'custom') {
            // Custom evaluator metrics discovered dynamically
            customConfigs.forEach((cfg) => {
              metrics[cfg.key] = getTestCaseMetric(r, cfg.key);
            });
          }
          
          return (
            <div key={idx} className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-slate-500 dark:text-slate-400">Line {r.line_number ?? idx}</div>
                <div className="text-xs dark:text-slate-300">{Object.entries(r).filter(([k]) => k.startsWith('outputs.')).length} metrics</div>
              </div>
              
              {/* Metric Badges Section */}
              <div className="mb-3">
                <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                  {qualityFilter === 'assisted' ? 'AI Quality Metrics' : qualityFilter === 'nlp' ? 'NLP Metrics' : 'Custom Evaluators'}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {qualityFilter === 'assisted' ? 
                    assistedConfigs.map((cfg) => (
                      <React.Fragment key={cfg.key}>
                        {renderMetricBadge(cfg.key, metrics[cfg.key])}
                      </React.Fragment>
                    )) : qualityFilter === 'nlp' ? 
                    nlpConfigs.map((cfg) => (
                      <React.Fragment key={cfg.key}>
                        {renderMetricBadge(cfg.key, metrics[cfg.key])}
                      </React.Fragment>
                    )) : 
                    customConfigs.map((cfg) => (
                      <React.Fragment key={cfg.key}>
                        {renderMetricBadge(cfg.key, metrics[cfg.key])}
                      </React.Fragment>
                    ))
                  }
                </div>
              </div>
              
              <Tabs defaultValue="summary">
                <TabsList>
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="full">Full</TabsTrigger>
                </TabsList>
                <TabsContent value="summary" className="mt-2">
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="text-slate-500 dark:text-slate-400">Query</div>
                      <div className="text-slate-800 dark:text-slate-200 break-words">{r["inputs.query"] || r.inputs?.query || ''}</div>
                    </div>
                    <div>
                      <div className="text-slate-500 dark:text-slate-400">Response</div>
                      <div className="text-slate-800 dark:text-slate-200 break-words">{r["inputs.response"] || r.inputs?.response || ''}</div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-slate-500 dark:text-slate-400">Context</div>
                      <div className="text-slate-800 dark:text-slate-200 break-words text-xs">{r["inputs.context"] || r.inputs?.context || ''}</div>
                    </div>
                  </div>
                  <div className="mt-3 grid md:grid-cols-3 gap-2 text-xs">
                    {Object.entries(r).filter(([k]) => k.startsWith('outputs.')).map(([k, v]) => (
                      <div key={k} className="p-2 rounded border dark:border-slate-700">
                        <div className="text-slate-600 dark:text-slate-300 truncate" title={k}>{k}</div>
                        <div className="text-slate-800 dark:text-slate-200 font-semibold">{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="full" className="mt-2">
                  <pre className="text-xs bg-slate-900 text-slate-200 p-3 rounded max-h-96 overflow-auto">{JSON.stringify(r, null, 2)}</pre>
                </TabsContent>
              </Tabs>
            </div>
          );
        })}
      </div>
    );
  };

  const exportResults = () => {
    const data = {
      runs: filteredRuns,
      selectedRun: selected,
      assistedSummary,
      nlpSummary,
      trendData,
      allMetricConfigs,
    };
    
    // Export as JSON (default)
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evaluation_results_${selectedId || 'all'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportAsCSV = () => {
    if (!selected) return;
    
    // Create CSV content for the selected run
    const headers = ['Test Case', 'Similarity', 'Numerical Correctness', 'Groundedness', 'Relevance', 'Coherence', 'ROUGE Precision', 'ROUGE Recall'];
    const rows = selected.rows?.map(row => [
      row.inputs?.user_input || 'N/A',
      row.outputs?.similarity?.similarity || row.similarity?.similarity || 'N/A',
      row.outputs?.numerical_correctness?.score || row.numerical_correctness?.score || row.outputs?.numeric_correctness?.score || row.numeric_correctness?.score || 'N/A',
      row.outputs?.groundedness?.groundedness || row.groundedness?.groundedness || 'N/A',
      row.outputs?.relevance?.relevance || row.relevance?.relevance || 'N/A',
      row.outputs?.coherence?.coherence || row.coherence?.coherence || 'N/A',
      row.outputs?.rouge?.rouge_precision || row.rouge?.rouge_precision || 'N/A',
      row.outputs?.rouge?.rouge_recall || row.rouge?.rouge_recall || 'N/A'
    ]) || [];
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evaluation_results_${selectedId}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportAsPDF = () => {
    // For PDF export, we'll create a simple text-based summary
    // In a real implementation, you might use a library like jsPDF
    if (!selected) return;
    
    const summary = `
Evaluation Results Summary
Run ID: ${selected.id}
Status: ${selected.status}
Environment: ${selected.options?.environment || selected.environment || 'N/A'}
Test Count: ${selected.rows?.length || 0}
Date: ${new Date(selected.updated_at || selected.created_at).toLocaleDateString()}

Metrics Summary:
${assistedSummary.map(s => `${s.label}: ${Math.round(s.avg * 100)}% (${s.passed}/${s.total} passed)`).join('\n')}

NLP Metrics:
${nlpSummary.map(s => `${s.label}: ${Math.round(s.avg * 100)}% (${s.passed}/${s.total} passed)`).join('\n')}
    `.trim();
    
    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evaluation_summary_${selectedId}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportAsExcel = () => {
    // Build a workbook with two sheets: Summary and Raw Results
    const wb = XLSX.utils.book_new();

    // 1) Summary sheet
    const rows = selected?.results?.rows || selected?.rows || [];
    const toRow = (s) => ({ Metric: s.label, Average_pct: Math.round(s.avg * 100), Passed: s.passed, Total: s.total });
    const summaryData = [
      { Section: 'Run', Metric: 'Run ID', Value: selected?.id || '' },
      { Section: 'Run', Metric: 'Status', Value: selected?.status || '' },
      { Section: 'Run', Metric: 'Environment', Value: selected?.options?.environment || selected?.environment || '' },
      { Section: 'Run', Metric: 'Test Count', Value: rows.length },
      { Section: 'Run', Metric: 'Updated', Value: formatDateTimeInTz(selected?.updated_at || selected?.created_at) },
      {},
      { Section: 'AI Assisted' },
      ...assistedSummary.map(toRow),
      {},
      { Section: 'NLP' },
      ...nlpSummary.map(toRow),
      ...(customConfigs.length ? [{}, { Section: 'Custom Evaluators' }, ...customSummary.map(toRow)] : []),
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryData, { skipHeader: false });
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // 2) Raw Results sheet: flatten commonly used fields and include detected metrics
    const metricList = [
      ...assistedSummary.map((s) => s.label),
      ...nlpSummary.map((s) => s.label),
      ...customSummary.map((s) => s.label),
    ];

    const labelByKey = new Map();
    assistedSummary.forEach((s) => labelByKey.set(s.key, s.label));
    nlpSummary.forEach((s) => labelByKey.set(s.key, s.label));
    customConfigs.forEach((c) => labelByKey.set(c.key, c.label));

    const toMetricValue = (row, cfg) => {
      let v = getRowMetric(row, cfg);
      if (typeof v !== 'number' || Number.isNaN(v)) return '';
      if (typeof cfg.transform === 'function') v = cfg.transform(v);
      return Number.isFinite(v) ? Number(v.toFixed(4)) : '';
    };

    const rawRows = rows.map((r, idx) => {
      const base = {
        Line: r.line_number ?? idx,
        Query: r['inputs.query'] || r.inputs?.query || '',
        Response: r['inputs.response'] || r.inputs?.response || '',
      };
      const metricsObj = {};
      // Assisted
      assistedSummary.forEach((s) => {
        const cfg = assistedConfigs.find((c) => c.key === s.key);
        if (cfg) metricsObj[labelByKey.get(s.key) || s.key] = toMetricValue(r, cfg);
      });
      // NLP
      nlpSummary.forEach((s) => {
        const cfg = nlpConfigs.find((c) => c.key === s.key);
        if (cfg) metricsObj[labelByKey.get(s.key) || s.key] = toMetricValue(r, cfg);
      });
      // Custom
      customConfigs.forEach((cfg) => {
        metricsObj[cfg.label] = toMetricValue(r, cfg);
      });
      return { ...base, ...metricsObj };
    });
    const rawSheet = XLSX.utils.json_to_sheet(rawRows, { skipHeader: false });
    XLSX.utils.book_append_sheet(wb, rawSheet, 'Raw Results');

    const fname = `evaluation_${selectedId || 'all'}_${new Date().toISOString().slice(0,10)}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 p-4 md:p-8" data-tour="evalresults-filters">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Evaluation Results</h1>
            {/* Auto-refresh status indicator */}
            {runs.some(run => run.status === 'queued' || run.status === 'started' || run.status === 'running') && (
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>Auto-refreshing</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2" data-tour="evalresults-export">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 border rounded-lg transition-all duration-200 flex items-center gap-2 font-medium text-sm ${
                hasActiveFilters 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 border-blue-200 dark:border-blue-800' 
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
              title="Toggle filters"
            >
              <ListFilter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-500 rounded-full">
                  {[statusFilter, environmentFilter, ragFilter, evaluatorFilter, timeRangeFilter, collectionFilter].filter(f => f !== "all").length + (search ? 1 : 0)}
                </span>
              )}
            </button>
            <button
              onClick={() => loadRuns(true)}
              className="px-3 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:shadow-sm rounded-lg transition-all duration-200 flex items-center gap-2 font-medium text-sm"
              title="Refresh runs list"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <Input
              placeholder="Search runs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
              data-tour="evalresults-search"
            />
            <div className="relative">
              <button
                onClick={() => document.getElementById('exportDropdown').classList.toggle('hidden')}
                className="px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:shadow-sm rounded-lg transition-all duration-200 flex items-center gap-2 font-medium text-sm"
                title="Export results in multiple formats"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div id="exportDropdown" className="hidden absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50">
                <div className="py-1">
                  <button
                    onClick={() => { exportResults(); document.getElementById('exportDropdown').classList.add('hidden'); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors duration-150"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export as JSON
                  </button>
                  <button
                    onClick={() => { exportAsCSV(); document.getElementById('exportDropdown').classList.add('hidden'); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors duration-150"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export as CSV
                  </button>
                  <button
                    onClick={() => { exportAsExcel(); document.getElementById('exportDropdown').classList.add('hidden'); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors duration-150"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 4H8a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V6a2 2 0 00-2-2zM10 9h4M10 13h4" />
                    </svg>
                    Export as Excel
                  </button>
                  <button
                    onClick={() => { exportAsPDF(); document.getElementById('exportDropdown').classList.add('hidden'); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors duration-150"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Export as Text Summary
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <Card className="shadow-sm border-0 bg-white/80 dark:bg-slate-900/60 backdrop-blur-sm mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg dark:text-slate-100">Filter Results</CardTitle>
                {hasActiveFilters && (
                  <button
                    onClick={handleClearFilters}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Clear all
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-6">
              {/* Primary Filters */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Run Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 block">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                    >
                      <option value="all">All Statuses</option>
                      {getUniqueStatuses().map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  {/* Environment Filter */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 block">Environment</label>
                    <select
                      value={environmentFilter}
                      onChange={(e) => setEnvironmentFilter(e.target.value)}
                      className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                    >
                      <option value="all">All Environments</option>
                      {getUniqueEnvironments().map(env => (
                        <option key={env} value={env}>{env}</option>
                      ))}
                    </select>
                  </div>

                  {/* RAG Filter */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 block">RAG Mode</label>
                    <select
                      value={ragFilter}
                      onChange={(e) => setRagFilter(e.target.value)}
                      className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                    >
                      <option value="all">All RAG Modes</option>
                      <option value="enabled">RAG Enabled</option>
                      <option value="disabled">RAG Disabled</option>
                    </select>
                  </div>

                  {/* Evaluator Filter */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 block">Evaluator</label>
                    <select
                      value={evaluatorFilter}
                      onChange={(e) => setEvaluatorFilter(e.target.value)}
                      className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                    >
                      <option value="all">All Evaluators</option>
                      {getUniqueEvaluators().map(evaluator => (
                        <option key={evaluator} value={evaluator}>{evaluator}</option>
                      ))}
                    </select>
                  </div>

                  {/* Time Range Filter */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 block">Time Range</label>
                    <select
                      value={timeRangeFilter}
                      onChange={(e) => setTimeRangeFilter(e.target.value)}
                      className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                    >
                      <option value="all">All Time</option>
                      <option value="1h">Last Hour</option>
                      <option value="6h">Last 6 Hours</option>
                      <option value="24h">Last 24 Hours</option>
                      <option value="7d">Last 7 Days</option>
                      <option value="30d">Last 30 Days</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Collection Filter - Separate Section */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
                  Test Case Collections
                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                    (Filter by dataset used in runs)
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 block">Collection</label>
                    <select
                      value={collectionFilter}
                      onChange={(e) => setCollectionFilter(e.target.value)}
                      className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                      disabled={remoteLoading}
                    >
                      <option value="all">All Collections</option>
                      {remoteCollections
                        .filter(collection => Array.isArray(collection.criteria?.ids) && collection.criteria.ids.length > 0)
                        .map(collection => {
                          const matchCount = runs.filter(run => run.options?.collection_id === collection.id).length;
                          return (
                            <option key={collection.id} value={collection.id}>
                              {collection.name} ({matchCount} runs)
                            </option>
                          );
                        })}
                    </select>
                    {remoteLoading && (
                      <div className="text-xs text-slate-500 mt-1">Loading collections...</div>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                    <div><strong>Org ID:</strong> {orgIdForCollections || "Not detected"}</div>
                    <div><strong>Collections found:</strong> {remoteCollections.length}</div>
                    <div><strong>Selection-based:</strong> {remoteCollections.filter(c => Array.isArray(c.criteria?.ids)).length}</div>
                    <div><strong>Collection filtering:</strong> Now using stored collection_id from runs</div>
                  </div>
                </div>
              </div>
              
              {/* Filter Summary */}
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Showing {filteredRuns.length} of {runs.length} runs
                  {hasActiveFilters && (
                    <span className="ml-2 text-blue-600 dark:text-blue-400">
                      (filtered)
                    </span>
                  )}
                  {collectionFilter !== "all" && (
                    <div className="mt-2 text-xs">
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                        Collection: {remoteCollections.find(c => c.id === collectionFilter)?.name || collectionFilter}
                      </span>
                      <div className="mt-1 text-slate-500 dark:text-slate-400">
                        Trends are filtered to runs using the same test case collection for meaningful comparison.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="shadow-sm border-0 bg-white/80 dark:bg-slate-900/60 backdrop-blur-sm mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg dark:text-slate-100">
                Overview trends
                {filteredRuns.length < runs.length && (
                  <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
                    ({filteredRuns.length} runs)
                  </span>
                )}
              </CardTitle>
              {collectionFilter !== "all" && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs">
                  Collection: {remoteCollections.find(c => c.id === collectionFilter)?.name || collectionFilter}
                </span>
              )}
            </div>
            {hasActiveFilters && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Trend data filtered by active filters{collectionFilter !== "all" ? " - showing trends for consistent dataset" : ""}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-4">
            {trendLoading && <div className="text-sm text-slate-600 dark:text-slate-300">Loading trends…</div>}
            <div className="grid md:grid-cols-4 gap-4">
              {assistedConfigs.map((cfg) => (
                <div key={cfg.key} className="p-3 rounded-lg border bg-white dark:bg-slate-900/60">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{cfg.label}</div>
                    {trendData[cfg.key] && trendData[cfg.key].length > 0 && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {trendData[cfg.key].length} pts
                      </div>
                    )}
                  </div>
                  <SparklineWithDots
                    series={trendData[cfg.key]}
                    onPointClick={(pt) => setSelectedId(pt.id)}
                    valueFormatter={(pt) => `${(pt.raw ?? 0).toFixed(2)}`}
                    yMin={cfg.yMin}
                    yMax={cfg.yMax}
                    invertYAxis={false}
                  />
                </div>
              ))}
              {nlpConfigs.map((cfg) => (
                <div key={cfg.key} className="p-3 rounded-lg border bg-white dark:bg-slate-900/60">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{cfg.label}</div>
                    {trendData[cfg.key] && trendData[cfg.key].length > 0 && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {trendData[cfg.key].length} pts
                      </div>
                    )}
                  </div>
                  <SparklineWithDots
                    series={trendData[cfg.key]}
                    stroke="#6366f1"
                    dot="#6366f1"
                    onPointClick={(pt) => setSelectedId(pt.id)}
                    valueFormatter={(pt) => `${(pt.raw ?? 0).toFixed(2)}`}
                    yMin={cfg.yMin}
                    yMax={cfg.yMax}
                    invertYAxis={false}
                  />
                </div>
              ))}
              {customConfigs.map((cfg) => (
                <div key={cfg.key} className="p-3 rounded-lg border bg-white dark:bg-slate-900/60">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{cfg.label}</div>
                    {trendData[cfg.key] && trendData[cfg.key].length > 0 && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {trendData[cfg.key].length} pts
                      </div>
                    )}
                  </div>
                  <SparklineWithDots
                    series={trendData[cfg.key]}
                    stroke="#f59e0b"
                    dot="#f59e0b"
                    onPointClick={(pt) => setSelectedId(pt.id)}
                    valueFormatter={(pt) => `${(pt.raw ?? 0).toFixed(2)}`}
                    yMin={cfg.yMin}
                    yMax={cfg.yMax}
                    invertYAxis={false}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1 space-y-2" style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', paddingRight: '4px' }} data-tour="evalresults-overview">
            {filteredRuns.map((r) => (
              <div key={r.id} onClick={() => setSelectedId(r.id)} className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${selectedId === r.id ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/20 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60'}`} data-tour="evalresults-comparison">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{r.id}</div>
                    <div className="text-xs mt-1 leading-relaxed">
                      {(r.options?.notes || r.options?.title || r.title || r.notes) ? (
                        <div className="text-slate-600 dark:text-slate-400" title={r.options?.notes || r.options?.title || r.title || r.notes}>
                          <span className="inline-flex items-center">
                            <svg className="w-3 h-3 mr-1 opacity-60" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                            </svg>
                            <span className="truncate">{r.options?.notes || r.options?.title || r.title || r.notes}</span>
                          </span>
                        </div>
                      ) : (
                        <div className="text-slate-400 dark:text-slate-500 italic">
                          <span className="inline-flex items-center">
                            <svg className="w-3 h-3 mr-1 opacity-40" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                            </svg>
                            <span className="truncate">No title</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                                          {statusBadge(r.status)}
                      </div>
                      {getRunTroubleshootingInfo(r)}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {(r.options?.environment || r.environment) && (
                    <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 font-medium">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                      </svg>
                      {r.options?.environment || r.environment}
                    </span>
                  )}
                  {r.testCount && (
                    <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-medium">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm3 8a1 1 0 102 0v-1a1 1 0 100-2 1 1 0 100-2 1 1 0 10-2 0v5zM11 7a1 1 0 100 2h3a1 1 0 100-2h-3zM10 9a1 1 0 011 1v1h3a1 1 0 110 2h-3a1 1 0 01-1-1v-1a1 1 0 100-2z" clipRule="evenodd"/>
                      </svg>
                      {r.testCount} tests
                    </span>
                  )}
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {formatDateTimeInTz(r.updated_at || r.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="md:col-span-2" style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
            {filteredRuns.length === 0 ? (
              <Card className="shadow-sm border-0 bg-white/80 dark:bg-slate-900/60 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <ListFilter className="w-8 h-8 text-slate-400 dark:text-slate-500" />
                    </div>
                    <CardTitle className="text-xl text-slate-700 dark:text-slate-200 mb-2">
                      No runs match the current filters
                    </CardTitle>
                    <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                      Adjust the filters above to see run results, or clear all filters to view everything.
                    </p>
                    {hasActiveFilters && (
                      <button
                        onClick={handleClearFilters}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 border border-slate-200 dark:border-slate-600"
                      >
                        <X className="w-4 h-4" />
                        Clear all filters
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : !selectedId ? (
              <div className="text-sm text-slate-600 dark:text-slate-300">Select a run to view details.</div>
            ) : loading ? (
              <div className="text-sm text-slate-600 dark:text-slate-300">Loading…</div>
            ) : error ? (
              <div className="text-sm text-rose-700">{error}</div>
            ) : !selected ? (
              <div className="text-sm text-slate-600 dark:text-slate-300">No data.</div>
            ) : (
              <div className="space-y-4">
                <div className="sticky top-0 z-10 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm mb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 truncate">
                          Run {selected.id}
                        </h2>
                        {statusBadge(selected.status)}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {(selected.options?.environment || selected.environment) && (
                          <span className="inline-flex items-center text-sm px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 font-medium border border-blue-200 dark:border-blue-800">
                            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                            </svg>
                            {selected.options?.environment || selected.environment}
                          </span>
                        )}
                        {typeof (selected.options?.use_rag ?? selected.use_rag) === 'boolean' && (
                          <span className={`inline-flex items-center text-sm px-3 py-1 rounded-full font-medium border ${
                            (selected.options?.use_rag ?? selected.use_rag)
                              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                          }`}>
                            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 2a6 6 0 00-6 6v1H3a2 2 0 000 4h2.09A6.002 6.002 0 0010 18a6 6 0 005.91-5H18a2 2 0 000-4h-1V8a6 6 0 00-6-6z"/>
                            </svg>
                            { (selected.options?.use_rag ?? selected.use_rag) ? 'RAG ON' : 'RAG OFF' }
                          </span>
                        )}
                        {Number.isFinite(selected.options?.concurrency || selected.concurrency) && (
                          <span className="inline-flex items-center text-sm px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 font-medium border border-amber-200 dark:border-amber-800">
                            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3 3h4v4H3V3zm5 0h4v4H8V3zm5 0h4v4h-4V3zM3 8h4v4H3V8zm5 0h4v4H8V8zm5 0h4v4h-4V8zM3 13h4v4H3v-4zm5 0h4v4H8v-4zm5 0h4v4h-4v-4z"/>
                            </svg>
                            {selected.options?.concurrency || selected.concurrency}x
                          </span>
                        )}
                        {Array.isArray(selected.options?.evaluators || selected.evaluators) && (selected.options?.evaluators || selected.evaluators)?.length > 0 && (
                          <span className="inline-flex items-center text-sm px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium border border-slate-200 dark:border-slate-700">
                            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M13 7H7v6h6V7z"/>
                            </svg>
                            {(selected.options?.evaluators || selected.evaluators).length} evals
                          </span>
                        )}
                        {(selected.options?.notes || selected.options?.title || selected.title || selected.notes) && (
                          <span className="inline-flex items-center text-sm px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 font-medium border border-purple-200 dark:border-purple-800" title={selected.options?.notes || selected.options?.title || selected.title || selected.notes}>
                            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
                            </svg>
                            <span className="truncate max-w-[200px]">{selected.options?.notes || selected.options?.title || selected.title || selected.notes}</span>
                          </span>
                        )}
                        {(() => {
                          const rowCount = selected?.results?.rows?.length || selected?.rows?.length || 0;
                          return rowCount > 0 && (
                            <span className="inline-flex items-center text-sm px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-medium border border-green-200 dark:border-green-800">
                              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm3 8a1 1 0 102 0v-1a1 1 0 100-2 1 1 0 100-2 1 1 0 10-2 0v5zM11 7a1 1 0 100 2h3a1 1 0 100-2h-3zM10 9a1 1 0 011 1v1h3a1 1 0 110 2h-3a1 1 0 01-1-1v-1a1 1 0 100-2z" clipRule="evenodd"/>
                              </svg>
                              {rowCount} tests
                            </span>
                          );
                        })()}
                        {(selected.updated_at || selected.created_at) && (
                          <span className="inline-flex items-center text-sm px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                            </svg>
                            {formatDateTimeInTz(selected.updated_at || selected.created_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {selected.error && (
                  <div className="text-sm text-rose-700">{selected.error}</div>
                )}
                <div className="p-3 rounded-lg border bg-white dark:bg-slate-900/60" data-tour="evalresults-metrics">
                  <Tabs value={detailTab} onValueChange={setDetailTab}>
                    <TabsList>
                      <TabsTrigger value="metrics">Metric dashboard</TabsTrigger>
                      <TabsTrigger value="options">Options</TabsTrigger>
                    </TabsList>
                    <TabsContent value="metrics" className="mt-2">
                      {metricDashboard()}
                    </TabsContent>
                    <TabsContent value="options" className="mt-2">
                      {selected?.options && Object.keys(selected.options).length ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {typeof selected.options.use_rag === 'boolean' && (
                              <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium border ${
                                selected.options.use_rag
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800'
                                  : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                              }`}>
                                {selected.options.use_rag ? 'RAG ON' : 'RAG OFF'}
                              </span>
                            )}
                            {Number.isFinite(selected.options.concurrency) && (
                              <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 font-medium border border-amber-200 dark:border-amber-800">
                                {selected.options.concurrency}x concurrency
                              </span>
                            )}
                            {Array.isArray(selected.options.evaluators) && selected.options.evaluators.length > 0 && (
                              <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium border border-slate-200 dark:border-slate-700">
                                {selected.options.evaluators.length} evaluators
                              </span>
                            )}
                          </div>
                          <pre className="text-xs bg-slate-900 text-slate-200 p-2 rounded max-h-64 overflow-auto">{JSON.stringify(selected.options || {}, null, 2)}</pre>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-600 dark:text-slate-300">No options were captured for this run.</div>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
                
                {/* Individual Test Case Metrics Summary */}
                <div className="p-3 rounded-lg border bg-white dark:bg-slate-900/60">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">Test Case Metrics Summary</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(qualityFilter === 'assisted' ? assistedConfigs : qualityFilter === 'nlp' ? nlpConfigs : customConfigs).map((config) => {
                      const rows = selected?.results?.rows || selected?.rows || [];
                      const values = rows
                        .map((r) => getRowMetric(r, config))
                        .filter((v) => typeof v === 'number' && !Number.isNaN(v))
                        .map((v) => (typeof config.transform === 'function' ? config.transform(v) : v));
                      const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
                      const passed = values.filter((v) => config.pass(v)).length;
                      const total = values.length;
                      const description = getEvaluatorDescription(config.key);
                      const category = getEvaluatorCategory(config.key);
                      
                      return (
                        <div key={config.key} className="relative group">
                          <div className="p-3 rounded-lg border dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                            <div className="flex items-center gap-1 mb-2">
                              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{config.label}</div>
                              {description && <Info className="w-3 h-3 text-slate-400" />}
                            </div>
                            <div className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">
                              {avg.toFixed(2)}
                            </div>
                            <div className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                              {passed}/{total} passed
                            </div>
                            <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                              <div 
                                className={`h-full ${config.pass(avg) ? 'bg-emerald-500' : 'bg-rose-500'}`} 
                                style={{ width: `${Math.min(100, (avg / config.max) * 100)}%` }} 
                              />
                            </div>
                          </div>
                          
                          {/* Tooltip */}
                          {description && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 max-w-xs">
                              <div className="font-medium text-emerald-300 mb-1">{category}</div>
                              <div>{description}</div>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="p-3 rounded-lg border bg-white dark:bg-slate-900/60" data-tour="evalresults-testcases">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Rows</div>
                  {rowsTable()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


